import React from 'react';
import { useTranslation } from 'react-i18next';

import { Panel, SectionTitle, Field, Select, Button } from '../../../../components';
import { CONTACT_TOPICS } from '../../api';
import type { ContactErrors } from '../../useContactForm';
import type { ContactFormValues } from '../../api';
import {
  Form,
  Intro,
  Group,
  GroupTitle,
  Grid,
  FieldCell,
  FullCell,
  FieldError,
  Divider,
  MsgRoot,
  MsgLabel,
  MsgBox,
  MsgInput,
  CharCount,
  SendError,
  Footer,
} from './ContactForm.styles';

/** Soft cap on the message length (a generous support ticket). */
const MESSAGE_MAX = 2000;

export type ContactFormProps = {
  values: ContactFormValues;
  errors: ContactErrors;
  dirty: boolean;
  sending: boolean;
  sendError: string | null;
  justSent: boolean;
  onChange: (key: keyof ContactFormValues, value: string) => void;
  onSubmit: () => void;
};

/**
 * The Contact form. Reuses the same inputs as the Settings Billing / Profile
 * forms (`Field`, `Select`, `Button`, wrapped in a `Panel`) so it matches them
 * 1:1, with a page-local textarea for the message. All copy is translation-ready;
 * colours/spacing come from the theme tokens, so it follows dark / light + accents.
 */
export const ContactForm = ({
  values,
  errors,
  dirty,
  sending,
  sendError,
  justSent,
  onChange,
  onSubmit,
}: ContactFormProps): React.ReactElement => {
  const { t } = useTranslation();

  const topicOptions = CONTACT_TOPICS.map((option) => ({ value: option.value, label: option.label }));
  const remaining = MESSAGE_MAX - values.message.length;
  const sendLabel = sending ? t('Sending…') : justSent ? t('Message sent ✓') : t('Send message');

  return (
    <Panel>
      <SectionTitle title={t('Contact')} icon="info" />
      <Form>
        <Intro
          text="Have a question or run into something? Send us a message and the Whitelabels.com team will get back to you by email."
          tag="span"
        />

        <Group>
          <GroupTitle text="Your details" tag="span" />
          <Grid>
            <FieldCell>
              <Field label={t('Name')} value={values.name} onChange={(value) => onChange('name', value)} />
              {errors.name ? <FieldError text={errors.name} tag="span" /> : null}
            </FieldCell>
            <FieldCell>
              <Field
                label={t('Email address')}
                value={values.email}
                placeholder={t('you@example.com')}
                onChange={(value) => onChange('email', value)}
              />
              {errors.email ? <FieldError text={errors.email} tag="span" /> : null}
            </FieldCell>
          </Grid>
        </Group>

        <Divider />

        <Group>
          <GroupTitle text="How can we help?" tag="span" />
          <Grid>
            <FieldCell>
              <Select
                label={t('Topic')}
                value={values.topic}
                options={topicOptions}
                onChange={(value) => onChange('topic', value)}
              />
            </FieldCell>
            <FieldCell>
              <Field
                label={t('Subject')}
                value={values.subject}
                placeholder={t('A short summary')}
                onChange={(value) => onChange('subject', value)}
              />
              {errors.subject ? <FieldError text={errors.subject} tag="span" /> : null}
            </FieldCell>
            <FullCell>
              <MsgRoot>
                <MsgLabel text={t('Message')} tag="span" />
                <MsgBox $invalid={Boolean(errors.message)}>
                  <MsgInput
                    value={values.message}
                    maxLength={MESSAGE_MAX}
                    aria-label={t('Message')}
                    aria-invalid={errors.message ? true : undefined}
                    placeholder={t('Tell us what you need help with…')}
                    onChange={(event) => onChange('message', event.target.value)}
                  />
                </MsgBox>
                <CharCount
                  text="{{remaining}} characters left"
                  placeholders={{ remaining }}
                  tag="span"
                />
                {errors.message ? <FieldError text={errors.message} tag="span" /> : null}
              </MsgRoot>
            </FullCell>
          </Grid>
        </Group>

        {sendError ? <SendError text={sendError} tag="span" /> : null}

        <Footer>
          <Button label={sendLabel} fullWidth disabled={sending || !dirty} onClick={onSubmit} />
        </Footer>
      </Form>
    </Panel>
  );
};

export default ContactForm;